import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    buscarColaboradorConEstadoAPI,
    solicitarExcelContratosAPI,
    solicitarZipContratosAPI,
    getAllCargosAPI,
    getAllDepartamentosAPI,
    getSedesConRegionAPI
} from "../../consumers/backendApisUrls";
import { useManagePostRequest } from "../../hooks/useManagePostRequest/useManagePostRequest";
import { useGetOptionsSelect } from "../../hooks/useGetOptionsSelect/useGetOptionsSelect";
import { useGetRequest } from "../../hooks/useGetRequest/useGetRequest";
import { mostrarMensajeDeErrorConToast, responseCode200 } from "../../consumers/httpRequiestsUtils";
import MultiSelectAllInput from "../../components/MultiSelectInput/MultiSelectAllInput";
import InputSearchAsync from "../../components/modalComponents/InputSearchAsync";

// Estado.Activo = 1, Estado.Cesado = 5 (Intranet.Common.Enums.Estado)
const ESTADOS_COLABORADOR = [
    { value: 1, label: "Activo" },
    { value: 5, label: "Cesado" }
];

const DESCARGA = { Reporte: "reporte", Zip: "zip" };

// "Seleccionar todo" equivale a no filtrar: así no se excluye a quien no tiene región, sede o puesto asignado.
const idsParaFiltrar = (ids, options) =>
    options.length > 0 && options.every(o => ids.includes(o.value)) ? [] : ids;

const ReporteContratos = () => {
    const seccionName = "ReporteContratos";
    const [managePostRequest] = useManagePostRequest();
    const [getOptions] = useGetOptionsSelect();
    const [executeGetRequest] = useGetRequest();

    const [regionesOptions, setRegionesOptions] = useState([]);
    const [sedesOptions, setSedesOptions] = useState([]);
    const [puestosOptions, setPuestosOptions] = useState([]);

    const [idsRegiones, setIdsRegiones] = useState([]);
    const [idsSedes, setIdsSedes] = useState([]);
    const [idsPuestos, setIdsPuestos] = useState([]);
    const [idsEstados, setIdsEstados] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);

    const [descargando, setDescargando] = useState(null);

    // Sin región elegida (o con todas) se ofrecen todas las sedes; con regiones elegidas, solo las suyas.
    const sedesVisibles = useMemo(() => {
        const regionesElegidas = idsParaFiltrar(idsRegiones, regionesOptions);
        return regionesElegidas.length === 0
            ? sedesOptions
            : sedesOptions.filter(s => regionesElegidas.includes(s.idRegion));
    }, [idsRegiones, regionesOptions, sedesOptions]);

    // Al cambiar de región se quitan las sedes marcadas que ya no corresponden.
    useEffect(() => {
        setIdsSedes(prev => {
            const vigentes = prev.filter(id => sedesVisibles.some(s => s.value === id));
            return vigentes.length === prev.length ? prev : vigentes;
        });
    }, [sedesVisibles]);

    useEffect(() => {
        let unmounted = false;
        const init = async () => {
            const [regiones, sedesResponse, puestos] = await Promise.all([
                getOptions(getAllDepartamentosAPI()),
                executeGetRequest(getSedesConRegionAPI()),
                getOptions(getAllCargosAPI())
            ]);
            if (unmounted) return;
            setRegionesOptions(regiones ?? []);
            if (responseCode200(sedesResponse)) {
                // IdParent es la región de la sede: con ella se filtran las sedes según la región elegida.
                setSedesOptions(sedesResponse.data.map(s => ({ value: s.Value, label: s.Text, idRegion: s.IdParent })));
            } else {
                mostrarMensajeDeErrorConToast(sedesResponse);
            }
            setPuestosOptions(puestos ?? []);
        };
        init();

        return () => {
            unmounted = true;
        };
    }, []);

    const getPayload = () => ({
        "IdsDepartamentos": idsParaFiltrar(idsRegiones, regionesOptions),
        "IdsSedes": idsParaFiltrar(idsSedes, sedesVisibles),
        "IdsCargos": idsParaFiltrar(idsPuestos, puestosOptions),
        "IdsEstados": idsParaFiltrar(idsEstados, ESTADOS_COLABORADOR),
        "IdsEmpleados": colaboradores.map(o => o.value)
    });

    // Ni el Excel ni el ZIP se descargan aquí: el backend los genera en segundo plano y llegan por correo
    // (el Excel adjunto, el ZIP como enlace de descarga).
    const solicitar = async (tipo, api, mensaje) => {
        if (descargando) return;
        setDescargando(tipo);
        try {
            await managePostRequest(
                api,
                getPayload(),
                () => {
                    toast.success(
                        <div>
                            <strong>¡Solicitud enviada correctamente!</strong>
                            <p style={{ margin: "4px 0 0" }}>{mensaje}</p>
                        </div>
                    );
                },
                false
            );
        } finally {
            setDescargando(null);
        }
    };

    const solicitarExcel = () => solicitar(
        DESCARGA.Reporte,
        solicitarExcelContratosAPI(),
        "Estamos generando tu reporte. En unos minutos recibirás un correo electrónico con el archivo solicitado. Gracias por tu paciencia.");

    const solicitarZip = () => solicitar(
        DESCARGA.Zip,
        solicitarZipContratosAPI(),
        "Estamos preparando tu ZIP de contratos. En unos minutos recibirás un correo electrónico con el enlace de descarga. Gracias por tu paciencia.");

    return (
        <>
            <div className='card-body'>
                <div id={seccionName}>
                    <div className="form-row">
                        <div className="form-group col-md-6">
                            <label className="modal-label blue-standard-color reportes-label">Región</label>
                            <MultiSelectAllInput
                                options={regionesOptions}
                                selectedValues={idsRegiones}
                                setSelectedValues={setIdsRegiones}
                                placeholder="Seleccionar región" />
                        </div>
                        <div className="form-group col-md-6">
                            <label className="modal-label blue-standard-color reportes-label">Sede</label>
                            <MultiSelectAllInput
                                options={sedesVisibles}
                                selectedValues={idsSedes}
                                setSelectedValues={setIdsSedes}
                                placeholder="Seleccionar sede" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-md-6">
                            <label className="modal-label blue-standard-color reportes-label">Puesto</label>
                            <MultiSelectAllInput
                                options={puestosOptions}
                                selectedValues={idsPuestos}
                                setSelectedValues={setIdsPuestos}
                                placeholder="Seleccionar puesto" />
                        </div>
                        <div className="form-group col-md-6">
                            <label className="modal-label blue-standard-color reportes-label">Estado</label>
                            <MultiSelectAllInput
                                options={ESTADOS_COLABORADOR}
                                selectedValues={idsEstados}
                                setSelectedValues={setIdsEstados}
                                placeholder="Seleccionar estado"
                                textoTodos="Todos" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-12">
                            <InputSearchAsync
                                typeGet
                                isMulti
                                direction="column"
                                label="Colaborador"
                                api={buscarColaboradorConEstadoAPI}
                                name="IdsEmpleados"
                                sectionName={seccionName}
                                value={colaboradores}
                                placeholder="Buscar por nombre, código de trabajador o DNI..."
                                onChange={(e) => setColaboradores(Array.isArray(e.target.value) ? e.target.value : [])} />
                        </div>
                    </div>

                    <div className="reporte-datos-generales-dato-obligatorio">
                        <p>Los filtros son opcionales. Sin selección se descarga el reporte completo.</p>
                        <p>El Excel se genera en segundo plano y llega adjunto a tu correo; el ZIP llega como un enlace de descarga.</p>
                    </div>

                    <div>
                        <button
                            type="button"
                            className="btn btn-primary generic-button-ficha"
                            disabled={!!descargando}
                            onClick={solicitarExcel}>
                            {descargando === DESCARGA.Reporte ? 'Solicitando...' : 'Descargar Reporte'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary ml-3"
                            style={{ borderRadius: "15px" }}
                            disabled={!!descargando}
                            onClick={solicitarZip}>
                            {descargando === DESCARGA.Zip ? 'Solicitando...' : 'Descargar ZIP'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
};

export default ReporteContratos;
